import React, { useEffect, useMemo, useState } from "react";
import { Autocomplete, Button, CircularProgress, Stack, TextField } from "@mui/material";
import api from "../../services/api/axios";

type NaicsOption = { code: string; title: string; standard_sector?: string | null };
type Props = {
  value?: string;
  title?: string;
  level?: number;
  onChange: (_code: string, _title: string) => void;
  onSectorChange?: (_sector: string) => void;
};

export default function NaicsSelector({ value, title, level, onChange, onSectorChange }: Props) {
  const [options, setOptions] = useState<NaicsOption[]>([]);
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [retry, setRetry] = useState(0);

  useEffect(() => { setQuery(""); }, [value]);

  useEffect(() => {
    const controller = new AbortController();
    let active = true;
    setLoading(true);
    setError(false);
    setOptions([]);
    const timer = setTimeout(async () => {
      try {
        const { data } = await api.get("/reference/naics/codes", {
          params: { q: query.trim() || undefined, level, limit: 100 },
          signal: controller.signal,
        });
        if (active) setOptions(Array.isArray(data) ? data : []);
      } catch {
        if (active) setError(true);
      } finally {
        if (active) setLoading(false);
      }
    }, 250);
    return () => {
      active = false;
      clearTimeout(timer);
      controller.abort();
    };
  }, [query, level, retry]);

  // Retain saved codes even when outside the current search results or no
  // longer present in the reference version. A lookup never edits the form.
  const selected = useMemo(() => value ? { code: value, title: title || "" } : null, [value, title]);
  const visibleOptions = selected && !options.some((option) => option.code === selected.code)
    ? [selected, ...options] : options;

  return (
    <Stack spacing={1}>
      <Autocomplete
        options={visibleOptions}
        value={selected}
        loading={loading}
        filterOptions={(items) => items}
        getOptionLabel={(option) => option.title ? `${option.code} — ${option.title}` : option.code}
        isOptionEqualToValue={(option, current) => option.code === current.code}
        onInputChange={(_event, input, reason) => {
          if (reason === "input" || reason === "clear") setQuery(input);
        }}
        onChange={(_event, option) => {
          setQuery("");
          onChange(option?.code || "", option?.title || "");
          if (option?.standard_sector) onSectorChange?.(option.standard_sector);
        }}
        noOptionsText={error ? "NAICS codes could not be loaded." : "No matching NAICS codes."}
        renderInput={(params) => (
          <TextField
            {...params}
            label="NAICS Code"
            error={error}
            helperText={error ? "Could not load NAICS codes. Please retry." :
              `Search by ${level ? `${level}-digit ` : ""}NAICS code or industry name.`}
            InputProps={{
              ...params.InputProps,
              endAdornment: <>{loading && <CircularProgress size={18} color="inherit" />}{params.InputProps.endAdornment}</>,
            }}
          />
        )}
      />
      {error && <Button size="small" onClick={() => setRetry((count) => count + 1)}>Retry lookup</Button>}
    </Stack>
  );
}
